// The protocol parts of a dictation (#23): "anamneza …" (the main complaints,
// kept as said) and "dijagnoza K02.1" (ICD-10 codes). Everything else is left
// for the tooth parser.
import type { Language } from '@/i18n';
import { fold } from './lexicon/fold';

type Keywords = { anamnesis: RegExp; diagnosis: RegExp; letters: Record<string, string> };

/** Folded (lower case, no accents) whole words that open each part, per language. */
const KEYWORDS: Record<Language, Keywords> = {
  sr: {
    anamnesis: /^anamnez[aeiu]?$/,
    diagnosis: /^dijagnoz[aeiu]?$/,
    letters: { ka: 'K', es: 'S', ce: 'C', de: 'D', zet: 'Z' },
  },
  hu: {
    anamnesis: /^anamnezis(e|t)?$/,
    diagnosis: /^diagnozis(a|t)?$/,
    letters: { ka: 'K', esz: 'S', ce: 'C', de: 'D', zet: 'Z' },
  },
  en: {
    anamnesis: /^anamnesis$/,
    diagnosis: /^diagnosis$/,
    letters: { kay: 'K', ess: 'S', see: 'C', dee: 'D', zed: 'Z', zee: 'Z' },
  },
  nb: {
    anamnesis: /^anamnesen?$/,
    diagnosis: /^diagnosen?$/,
    letters: { ko: 'K', es: 'S', se: 'C', de: 'D', set: 'Z' },
  },
};

/** Words that join several codes ("K02.1 i K04.5"). */
const JOINERS = new Set(['i', 'and', 'es', 'og', 'pa', 'te']);

export type Sections = {
  /** What was said after "anamneza", up to a "dijagnoza" (null: not in this text). */
  anamnesis: string | null;
  /** ICD-10 codes said after "dijagnoza" ("K02.1"), upper case. */
  diagnoses: string[];
  /** Everything else, for the tooth parser. */
  rest: string;
};

/** ICD-10 chapters a dental diagnosis falls in: oral (K), injury (S), neoplasm (C, D), contact (Z). */
const CHAPTERS = new Set(['K', 'S', 'C', 'D', 'Z']);

/** An FDI tooth number (11–48, primary 51–85). */
function isFdiTooth(n: number): boolean {
  const quadrant = Math.floor(n / 10);
  const position = n % 10;
  if (quadrant >= 1 && quadrant <= 4) return position >= 1 && position <= 8;
  if (quadrant >= 5 && quadrant <= 8) return position >= 1 && position <= 5;
  return false;
}

const build = (letter: string, group: string, sub?: string) =>
  `${letter}${group}${sub ? `.${sub}` : ''}`;

/**
 * One ICD-10 code at the start of `words`: "K02.1", "k021", "K 02.1",
 * "ka 02,1", "K 02 1". Returns the code and how many words it took.
 */
function readCode(words: readonly string[], letters: Record<string, string>) {
  const w = words.slice(0, 3).map((x) =>
    fold(x)
      .replace(/[;:!?]+$/, '')
      .replace(/[,.]$/, '')
  );
  const whole = /^([a-z])(\d{2})(?:[.,]?(\d{1,2}))?$/.exec(w[0] ?? '');
  if (whole) return { code: build(whole[1].toUpperCase(), whole[2], whole[3]), taken: 1 };
  // Apart, the letter must be a chapter dentistry uses ("u 46" is "in 46", not U46).
  const head = w[0] ?? '';
  const letter = letters[head] ?? (CHAPTERS.has(head.toUpperCase()) ? head.toUpperCase() : null);
  const group = letter ? /^(\d{2})(?:[.,]?(\d{1,2}))?$/.exec(w[1] ?? '') : null;
  if (!(letter && group)) return null;
  if (group[2]) return { code: build(letter, group[1], group[2]), taken: 2 };
  // "K 02 1": a lone digit is the subcategory (a tooth would have two).
  if (/^\d$/.test(w[2] ?? '')) return { code: build(letter, group[1], w[2]), taken: 3 };
  // "s 36" is "with 36": a bare tooth number after a letter stays a tooth.
  if (isFdiTooth(Number(group[1]))) return null;
  return { code: build(letter, group[1]), taken: 2 };
}

/** Codes from the start of `words`, joined by "i" / "and" / commas; returns the codes and the words left. */
function readCodes(words: readonly string[], letters: Record<string, string>) {
  const codes: string[] = [];
  let i = 0;
  while (i < words.length) {
    const found = readCode(words.slice(i), letters);
    if (!found) break;
    codes.push(found.code);
    i += found.taken;
    if (i < words.length && JOINERS.has(fold(words[i]).replace(/,$/, ''))) i += 1;
  }
  return { codes, left: words.slice(i) };
}

/**
 * Splits a dictated text into the anamnesis, the diagnosis codes and the rest.
 * `inside`: the text continues an anamnesis begun earlier in the utterance.
 */
export function readSections(text: string, lang: Language, inside = false): Sections {
  const kw = KEYWORDS[lang];
  const words = text.trim().split(/\s+/).filter(Boolean);
  const bare = (w: string) => fold(w).replace(/[.,:;!?]+$/, '');
  const rest: string[] = [];
  const anamnesis: string[] = [];
  const diagnoses: string[] = [];
  let inAnamnesis = inside;
  let i = 0;
  while (i < words.length) {
    const word = bare(words[i]);
    if (kw.anamnesis.test(word)) {
      inAnamnesis = true;
      i += 1;
      continue;
    }
    if (kw.diagnosis.test(word)) {
      inAnamnesis = false;
      const { codes, left } = readCodes(words.slice(i + 1), kw.letters);
      diagnoses.push(...codes.filter((c) => !diagnoses.includes(c)));
      i = words.length - left.length;
      continue;
    }
    (inAnamnesis ? anamnesis : rest).push(words[i]);
    i += 1;
  }
  const said = anamnesis.join(' ').replace(/^[:,\s]+/, '');
  const opened = inside || words.some((w) => kw.anamnesis.test(bare(w)));
  return {
    anamnesis: opened ? said : null,
    diagnoses,
    rest: rest.join(' '),
  };
}

/** The text ends inside an anamnesis: more words of the same utterance continue it. */
export function endsInAnamnesis(text: string, lang: Language, inside = false): boolean {
  const kw = KEYWORDS[lang];
  let open = inside;
  for (const w of text.trim().split(/\s+/)) {
    const word = fold(w).replace(/[.,:;!?]+$/, '');
    if (kw.anamnesis.test(word)) open = true;
    else if (kw.diagnosis.test(word)) open = false;
  }
  return open;
}

/** Sentence case for a dictated anamnesis ("bol kod 36" → "Bol kod 36."). */
export function asSentence(text: string): string {
  const t = text.trim();
  if (!t) return t;
  const first = t.charAt(0).toUpperCase() + t.slice(1);
  return /[.!?]$/.test(first) ? first : `${first}.`;
}
